import { TranslationKey } from "../i18n/translation-key"
import { PartCategory } from "../service/service-bike-part"

/**
 * Label keys for each part category, shown in the "Cykelskick" breakdown
 * on julo.se. Kept here so frontend and backend agree on the wording.
 */
export const PART_CATEGORY_LABEL_KEYS: Record<PartCategory, TranslationKey> = {
  [PartCategory.BRAKES]: "common.partCategory.brakes",
  [PartCategory.DRIVETRAIN]: "common.partCategory.drivetrain",
  [PartCategory.WHEELS_AND_TIRES]: "common.partCategory.wheelsAndTires",
  [PartCategory.FRAME_AND_SUSPENSION]: "common.partCategory.frameAndSuspension",
  [PartCategory.CONTACT_SURFACES]: "common.partCategory.contactSurfaces",
  [PartCategory.ELECTRICITY]: "common.partCategory.electricity",
  [PartCategory.OTHER]: "common.partCategory.other",
}

/** Canonical render order for the condition categories. */
export const PART_CATEGORY_ORDER: ReadonlyArray<PartCategory> = [
  PartCategory.BRAKES,
  PartCategory.DRIVETRAIN,
  PartCategory.WHEELS_AND_TIRES,
  PartCategory.FRAME_AND_SUSPENSION,
  PartCategory.CONTACT_SURFACES,
  PartCategory.ELECTRICITY,
  PartCategory.OTHER,
]

export interface BikeConditionPart {
  /** Part name shown when a category row is expanded, e.g. "Bromsbelägg fram". */
  name: string
  /** 0–100. */
  percent: number
}

export interface BikeConditionCategory {
  category: PartCategory
  /** 0–100. */
  percent: number
  /** The individual graded parts in this category, in input order. */
  parts: BikeConditionPart[]
}

export interface BikeCondition {
  /** Only categories that have at least one graded part, in canonical order. */
  byCategory: BikeConditionCategory[]
  /** 0–100, the average across all graded parts. */
  overallPercent: number
  /** Quality label key derived from {@link overallPercent}. */
  labelKey: TranslationKey
}

export interface GradedPart {
  category: PartCategory
  /** Part name, shown when a category row is expanded. */
  name: string
  /** Inspection grade, 0–10. */
  grade: number
}

/**
 * Quality label thresholds (inclusive lower bound, in percent). Tunable —
 * kept in one place so the listing badge and detail header stay in sync.
 */
const LOWEST_CONDITION_LABEL_KEY: TranslationKey = "common.bikeCondition.okay"

const CONDITION_LABEL_THRESHOLDS: ReadonlyArray<[number, TranslationKey]> = [
  [80, "common.bikeCondition.excellent"],
  [60, "common.bikeCondition.veryGood"],
  [40, "common.bikeCondition.good"],
  [0, LOWEST_CONDITION_LABEL_KEY],
]

export const bikeConditionLabelKey = (
  overallPercent: number,
): TranslationKey => {
  for (const [threshold, label] of CONDITION_LABEL_THRESHOLDS) {
    if (overallPercent >= threshold) {
      return label
    }
  }
  // Unreachable for a real percentage — the last threshold is 0.
  return LOWEST_CONDITION_LABEL_KEY
}

/**
 * Curated quality badge shown on the `/bikes` listing card. Only the two top
 * tiers earn a badge; everything else shows none.
 */
export enum BikeConditionBadge {
  NYSKICK = "NYSKICK",
  MYCKET_BRA_SKICK = "MYCKET_BRA_SKICK",
}

/** Every graded category must clear this percent for a badge to show at all. */
const BADGE_MIN_CATEGORY_PERCENT = 70

/**
 * Derives the listing badge from a computed condition. Returns null unless
 * every graded category is at least 70% AND the overall average is at least
 * 80% ("Mycket bra skick") or at least 90% ("Nyskick").
 */
export const bikeConditionBadge = (
  condition: BikeCondition,
): BikeConditionBadge | null => {
  if (condition.byCategory.length === 0) {
    return null
  }
  const allCategoriesGood = condition.byCategory.every(
    (category) => category.percent >= BADGE_MIN_CATEGORY_PERCENT,
  )
  if (!allCategoriesGood) {
    return null
  }
  if (condition.overallPercent >= 90) {
    return BikeConditionBadge.NYSKICK
  }
  if (condition.overallPercent >= 80) {
    return BikeConditionBadge.MYCKET_BRA_SKICK
  }
  return null
}

const average = (values: number[]): number =>
  values.reduce((sum, value) => sum + value, 0) / values.length

/** A grade is 0–10; the displayed condition is the grade as a percentage. */
const gradeToPercent = (grade: number): number => Math.round(grade * 10)

/**
 * Aggregates inspection part grades into per-category and overall condition
 * percentages plus a quality label. Categories with no graded parts are
 * omitted. Returns an empty breakdown and 0% when there are no graded parts.
 */
export const computeBikeCondition = (parts: GradedPart[]): BikeCondition => {
  const partsByCategory = new Map<PartCategory, GradedPart[]>()
  for (const part of parts) {
    const categoryParts = partsByCategory.get(part.category) ?? []
    categoryParts.push(part)
    partsByCategory.set(part.category, categoryParts)
  }

  const byCategory: BikeConditionCategory[] = []
  for (const category of PART_CATEGORY_ORDER) {
    const categoryParts = partsByCategory.get(category)
    if (!categoryParts || categoryParts.length === 0) {
      continue
    }
    byCategory.push({
      category,
      percent: gradeToPercent(average(categoryParts.map((p) => p.grade))),
      parts: categoryParts.map((p) => ({
        name: p.name,
        percent: gradeToPercent(p.grade),
      })),
    })
  }

  const overallPercent =
    parts.length === 0 ? 0 : gradeToPercent(average(parts.map((p) => p.grade)))

  return {
    byCategory,
    overallPercent,
    labelKey: bikeConditionLabelKey(overallPercent),
  }
}
